import { useCallback, useRef } from 'react'
import { TablePersistedState } from './types'
import { DEFAULT_SORT_STATE, DEFAULT_FILTER_STATE, DEFAULT_PAGE_SIZE } from './defaultsFromLegacy'

const SCHEMA_VERSION = 'v3'

interface PersistenceOptions {
  userId: string | number
  platformId: string | number
  channelId: string | number
}

export const useTablePersistence = ({ 
  userId, 
  platformId, 
  channelId
}: PersistenceOptions) => {
  const storageKey = `listing-product-table-lite:${SCHEMA_VERSION}:${userId}:${platformId}:${channelId}`
  const initializedRef = useRef(false)

  const loadPersistedState = useCallback((): Partial<TablePersistedState> | null => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (!stored) return null

      const parsed = JSON.parse(stored) as TablePersistedState
      
      // Validate schema version
      if (parsed.version !== SCHEMA_VERSION) {
        console.warn('Schema version mismatch, using defaults')
        localStorage.removeItem(storageKey)
        return null
      }

      return parsed
    } catch (error) {
      console.error('Error loading persisted table state:', error)
      return null
    }
  }, [storageKey])

  const savePersistedState = useCallback(
    (state: Partial<TablePersistedState>) => {
      if (!initializedRef.current) return // Don't save during initial hydration

      try {
        const stateToSave: TablePersistedState = {
          version: SCHEMA_VERSION,
          selectedColumns: state.selectedColumns || [],
          sortState: state.sortState || null,
          filterState: state.filterState || { globalSearch: '', columnFilters: [] },
          pageSize: state.pageSize || 25,
        }

        localStorage.setItem(storageKey, JSON.stringify(stateToSave))
      } catch (error) {
        console.error('Error saving table state:', error)
      }
    },
    [storageKey]
  )

  const clearPersistedState = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error('Error clearing table state:', error)
    }
  }, [storageKey])

  const getInitialState = useCallback(() => {
    const persisted = loadPersistedState()
    if (persisted && persisted.selectedColumns && persisted.selectedColumns.length > 0) {
      // Use persisted state if it has columns
      return persisted
    }
    // Return empty defaults - let useTableColumns set smart defaults based on platform type
    return {
      selectedColumns: [],
      sortState: DEFAULT_SORT_STATE,
      filterState: DEFAULT_FILTER_STATE,
      pageSize: DEFAULT_PAGE_SIZE,
    }
  }, [loadPersistedState])

  const markInitialized = useCallback(() => {
    initializedRef.current = true
  }, [])

  return {
    loadPersistedState,
    savePersistedState,
    clearPersistedState,
    getInitialState,
    markInitialized,
    isInitialized: () => initializedRef.current,
  }
}
